import { ReactNode } from 'react'
import { create } from 'zustand'

export type NotificationType = 'success' | 'info' | 'warning' | 'danger'
export interface UserNotification {
  type: NotificationType
  html: ReactNode
}

export type NotificationState = {
  notifications: UserNotification[]
  pushNotification: (notification: UserNotification) => void
  popNotification: () => void
}

/**
 * Manages user notifications: messages to indicate success, failure, etc.
 * for actions in the app.
 *
 * Notifications are kept in a simple queue, and are pushed to the end of the
 * queue when a new notification is added. The first notification in the queue
 * is popped when the user dismisses it.
 */
export const useNotifications = create<NotificationState>(set => ({
  notifications: [],
  pushNotification: (notification: UserNotification) =>
    set(({ notifications }) => ({
      notifications: [...notifications, notification],
    })),
  popNotification: () =>
    set(({ notifications }) => ({ notifications: notifications.slice(1) })),
}))

/**
 * Convenience hook to be able to push notifications
 * @returns a function that will push a new notification to the queue
 */
export function usePushNotification() {
  return useNotifications(getState)
}

const getState = ({ pushNotification }: NotificationState) => pushNotification
